import PropTypes from "prop-types";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import makeStyles from "@material-ui/core/styles/makeStyles";
import IconButton from "@material-ui/core/IconButton";
import Grid from "@material-ui/core/Grid";
import React, {useEffect} from "react";
import clsx from "clsx";
import Typography from "@material-ui/core/Typography";
import {CustomButton, CustomCTA, CloseIcon} from "@hapinest/theme";
import {useI18N} from "../../../../common/common";
import CustomAlert from "../../../widget/alerts/CustomAlert";
import NoIndexDialog from "../../noIndex/NoIndexDialog";

const useStyles = makeStyles((theme) => ({
    noPadding: {
        padding: 0
    },
    wrapper: {
        textAlign: "center",
        marginTop: theme.spacing(5.125),
        marginRight: theme.spacing(2),
        marginLeft: theme.spacing(2)
    },
    wrapperMarginTop: {
        marginTop: theme.spacing(2) + "!important",
    },
    wrapperMobile: {
        textAlign: "center",
        marginTop: theme.spacing(4.125),
        marginRight: theme.spacing(2),
        marginLeft: theme.spacing(2)
    },
    messageLabel: {
        marginTop: theme.spacing(5),
    },
    messageLabelMobile: {
        marginTop: theme.spacing(3),
    },
    button: {
        marginTop: theme.spacing(5),
        marginBottom: theme.spacing(5)
    },
    buttonMobile: {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(4)
    },
    buttonMobileSecond: {
        marginTop: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5)
    },
    emptyDiv: {
        height: theme.spacing(6)
    },
    buttonMarginLeftRight: {
        marginLeft: theme.spacing(2.5),
        marginRight: theme.spacing(2.5),
    },
    buttonMarginBottom: {
        marginBottom: theme.spacing(2)
    },
    buttonRed: {
        background: theme.palette.error.danger,
        color: theme.palette.grey["0"],
        "&:hover": {
            color: theme.palette.grey["0"],
            backgroundColor: theme.palette.error.danger + "!important"
        }
    },
    alert: {
        marginBottom: theme.spacing(2),
        marginLeft: theme.spacing(3),
        marginRight: theme.spacing(3),
        width: "auto"
    },
    titles: {
        color: theme.palette.grey["900"]
    },
    twoButtonContainer: {
        justifyContent: "center"
    }
}));

const CustomDialog = ({
    isOpen,
    onCloseDialog,
    isMobile,
    title,
    description,
    onButtonClick,
    onSecondButtonClick,
    buttonLoading,
    cta,
    buttonContained,
    imgSrc,
    imgAlt,
    wrapperMarginTop,
    xButton,
    secondButton,
    secondButtonOptions,
    isRedButton,
    errorMessage,
    color,
    disableBackdropClick,
    buttonStartIcon,
    buttonClass,
    buttonE2eId,
    secondButtonE2eId,
    isFullScreen,
    ...props
}) => {
    const classes = useStyles();
    const [open, setOpen] = React.useState(false);
    const {i18n} = useI18N();

    useEffect(() => {
        setOpen(isOpen);
    }, [isOpen]);

    const handleClose = () => {
        if (onCloseDialog) {
            onCloseDialog();
        }
    };

    const handleButtonClick = () => {
        if (onButtonClick) {
            onButtonClick();
        }
    };

    const handleSecondButtonClick = () => {
        if (onSecondButtonClick) {
            onSecondButtonClick();
        }
    };

    return(
        <NoIndexDialog
            open={open}
            fullScreen={isFullScreen}
            fullWidth
            onClose={handleClose}
            disableBackdropClick={disableBackdropClick}
            {...props}
        >
            <DialogTitle className={classes.noPadding}>
                <Grid
                    container
                    alignItems="center"
                    spacing={0}
                >
                    <Grid
                        item
                        md={12}
                        xs={12}
                        sm={12}
                        align={"right"}
                    >
                        {xButton ? (
                            <IconButton
                                aria-label="close"
                                onClick={handleClose}
                            >
                                <CloseIcon/>
                            </IconButton>
                        ) : (
                            <div className={classes.emptyDiv}/>
                        )
                        }
                    </Grid>

                </Grid>

            </DialogTitle>

            <DialogContent
                className={classes.noPadding}
            >
                {errorMessage &&
                    <CustomAlert
                        className={classes.alert}
                        type={"error"}
                    >
                        <Typography
                            variant="body1"
                            className={classes.titles}
                        >
                            {errorMessage}
                        </Typography>

                    </CustomAlert>
                }
                <Grid
                    container
                    alignItems="center"
                    justify="center"
                    spacing={0}
                >
                    <img
                        src={imgSrc}
                        alt={imgAlt}
                        width={80}
                        height={80}
                    />

                </Grid>

                <section
                    className={clsx({
                        [classes.wrapperMobile]: isMobile,
                        [classes.wrapper]: !isMobile,
                        [classes.wrapperMarginTop]: wrapperMarginTop
                    })}
                >
                    <Typography
                        style={{whiteSpace: "pre-wrap"}}
                        variant={isMobile ? "h6" : "h4"}
                    >
                        {title}
                    </Typography>

                    {description && typeof description === "string" &&
                    (
                        <Typography
                            variant="body1"
                            className={clsx({[classes.messageLabelMobile]: isMobile, [classes.messageLabel]: !isMobile})}
                        >
                            {description}
                        </Typography>
                    )}

                    {description && typeof description === "object" &&
                    (
                        description
                    )}

                </section>

                <Grid
                    container
                    justify="center"
                    className={clsx({[classes.buttonMobile]: isMobile && !secondButton,
                        [classes.button]: !isMobile,
                        [classes.buttonMobileSecond]: isMobile && secondButton})}
                >
                    {secondButton && (
                        <>
                            <CustomCTA
                                firstTitle={i18n(secondButtonOptions?.title) || i18n("common.label.cancel")}
                                firstSize={"medium"}
                                firstColor={"black"}
                                firstDisableElevation
                                firstClassNameOverride={buttonClass || classes.buttonMarginLeftRight}
                                firstActionHandler={onSecondButtonClick ? handleSecondButtonClick : handleClose}
                                secondTitle={cta}
                                secondVariant={buttonContained ? "contained" : "outlined"}
                                secondSize={"medium"}
                                secondClassNameOverride={secondButtonOptions?.class || clsx(classes.buttonMarginLeftRight, classes.buttonMarginBottom,
                                    {[classes.buttonRed]: isRedButton})}
                                secondActionHandler={handleButtonClick}
                                secondLoading={buttonLoading}
                                reverseMobileOrder
                                secondStartIcon={buttonStartIcon}
                                firstE2eId={buttonE2eId || ""}
                                secondE2eId={secondButtonE2eId || ""}
                            />
                        </>
                    )}

                    {!secondButton &&
                        <CustomButton
                            e2e-id={buttonE2eId || ""}
                            variant={buttonContained ? "contained" : "outlined"}
                            size={"medium"}
                            color={color}
                            disableElevation
                            className={clsx({[classes.buttonRed]: isRedButton})}
                            onClick={handleButtonClick}
                            loading={buttonLoading}
                            startIcon={buttonStartIcon}
                        >
                            {cta}
                        </CustomButton>
                    }
                </Grid>
            </DialogContent>
        </NoIndexDialog>
    );
};

CustomDialog.defaultProps = {
    isOpen: false,
    description: false,
    buttonLoading: false,
    buttonContained: false,
    imgSrc: "/images/action_failed.png",
    imgAlt: "Alert",
    wrapperMarginTop: false,
    xButton: false,
    secondButton: false,
    color: "primary",
    disableBackdropClick: true,
    isFullScreen: false
};

CustomDialog.propTypes = {
    isOpen: PropTypes.bool,
    onCloseDialog: PropTypes.func,
    isMobile: PropTypes.bool,
    title: PropTypes.any,
    description: PropTypes.any,
    onButtonClick: PropTypes.func,
    onSecondButtonClick: PropTypes.func,
    buttonLoading: PropTypes.bool,
    cta: PropTypes.string,
    buttonContained: PropTypes.bool,
    imgSrc: PropTypes.string,
    imgAlt: PropTypes.string,
    wrapperMarginTop: PropTypes.bool,
    xButton: PropTypes.bool,
    secondButton: PropTypes.bool,
    secondButtonOptions: PropTypes.any,
    isRedButton: PropTypes.bool,
    errorMessage: PropTypes.any,
    color: PropTypes.string,
    disableBackdropClick: PropTypes.bool,
    buttonStartIcon: PropTypes.object,
    buttonClass: PropTypes.string,
    buttonE2eId: PropTypes.string,
    secondButtonE2eId: PropTypes.string,
    isFullScreen: PropTypes.bool,
    props: PropTypes.any
};

export default CustomDialog;
